import { ReactElement, useEffect, useState } from "react";
import { Breadcrumb, Button, Layout, Space, Table, Avatar, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { BreadCrumbWrapper, RightButton } from "../../components/GlobalStyles";
import { CreateBranchDrawer } from "./CreateBranchDrawer";
import { EditBranchDrawer } from "./EditBranchDrawer";
import { DeleteBranchModal } from "./DeleteBranchModal";
import { getAuthState, getClubState, getBranchState } from "../../redux/selectors";
import { FETCH_BRANCHES_REQUEST } from "../../redux/constants";

const { Content } = Layout;
const shortOfDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; 

export function Club(): ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const { branches } = useSelector(getBranchState);
  const { clubs } = useSelector(getClubState);
  const { currentUser } = useSelector(getAuthState);
  const { clubId } = useParams<{ clubId: string }>();
  const [createBranchDrawerVisible, setCreateBranchDrawerVisible] = useState(false);
  const [editBranchDrawerVisible, setEditBranchDrawerVisible] = useState(false);
  const [deleteBranchModalVisible, setDeleteBranchModalVisible] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<any>();
  const [ hasPermission, setHasPermission ] = useState<boolean>(currentUser?.role === "admin" ? true :  clubId === currentUser?.club);
  const [ clubName, setClubName] = useState<string>();

  const { t } = useTranslation();

  useEffect(() => {
    dispatch({
      type: FETCH_BRANCHES_REQUEST,
      payload: { currentUser, clubId },
    });
    if(hasPermission){
      const { name } = clubs.filter((club) => club.key === clubId)[0];
      setClubName(name);
    }
  }, []);

  const onChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    // eslint-disable-next-line no-console
    console.log("params", pagination, filters, sorter, extra);
  };

  const onEdit = (record: any) => {
    setEditBranchDrawerVisible(true);
    setSelectedBranch(record);
  };

  const onDelete = (record: any) => {
    setDeleteBranchModalVisible(true);
    setSelectedBranch(record);
  };

  const goToBookingLogs = (record: any) => {
    history.push(`/clubs/${record.club}/bookings?branch=${record.firestoreRef}`);
  };

  const onView = (record: any) => {
    history.push(`/clubs/${record.club}/branch/${record.firestoreRef}`);
  };

  const renderFemaleOnly = (data: any) => {
    const day = shortOfDays[data.day];
    return (
      <li>{day}: {data.startTime}~{data.endTime}</li>
    )
  }

  const columns: any = [
    {
      title: "Photo",
      key: "photo",
      render: ({ photo }: any) => <Avatar src={photo} size={30} />,
    },
    {
      title: t("branch.location"),
      key: "location",
      render: ({ linkLocation, location }: any) => (
        <a href={linkLocation} target="_blank" rel="noreferrer">
          {location}
        </a>
      ),
    },
    {
      title: t("branch.available_hours"),
      key: "available",
      render: ({ startTime, endTime }: any) => (
        <Space size="middle">{`${startTime} - ${endTime}`}</Space>
      ),
    },
    {
      title: t("branch.services"),
      dataIndex: "services",
      key: "services",
    },
    {
      title: t("branch.created_at"),
      key: "createAt",
      render: ({ createAt }: any) => (
        <span>{moment(createAt).format("MMM DD, YYYY")}</span>
      ),
    },
    {
      title: "Court Count",
      key: "courtCount",
      render: ({ courtCount }: any) => (
        <span>
          {courtCount > 0 ? `${courtCount} courts` : "No courts"}
        </span>
      ),
    },
    {
      title: "Female Only",
      key: "femaleOnly",
      render: ({ femaleOnly }: any) => (
        femaleOnly.map((f: any) => (
          <ul>
            {renderFemaleOnly(f)}
          </ul>
        ))
      )
    },
    {
      title: t("common.action"),
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <Button onClick={() => onView(record)}>{t("court.courts")}</Button>
          <Button onClick={() => onEdit(record)}>{t("common.edit")}</Button>
          <Button onClick={() => goToBookingLogs(record)}>{t("club.bookings")}</Button>
          <Button danger onClick={() => onDelete(record)}>{t("common.delete")}</Button>
        </Space>
      ),
    },
  ];

  if(!hasPermission)
    return (
      <Content>
        <BreadCrumbWrapper>
          <Breadcrumb style={{ fontSize: 16 }}>
            <Breadcrumb.Item>
              <a href="/clubs">{t("club.clubs")}</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{clubName}</Breadcrumb.Item>
          </Breadcrumb>
        </BreadCrumbWrapper>
        <Row gutter={16}>
          <Col span={24}>
            <p>You have no permission for this page!</p>
          </Col>
        </Row>
      </Content>
    )

  return (
    <Content>
      <BreadCrumbWrapper>
        <Breadcrumb style={{ fontSize: 16 }}>
          <Breadcrumb.Item>
            <a href="/clubs">{t("club.clubs")}</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{clubName}</Breadcrumb.Item>
        </Breadcrumb>
        <RightButton
          type="primary"
          onClick={() => setCreateBranchDrawerVisible(true)}
        >
          {t("common.add")}
        </RightButton>
      </BreadCrumbWrapper>
      <Table
        columns={columns}
        dataSource={branches}
        onChange={onChange}
        rowKey="branchId"
      />
      <CreateBranchDrawer
        club={clubId}
        visible={createBranchDrawerVisible}
        onClose={() => setCreateBranchDrawerVisible(false)}
      />
      {selectedBranch && (
        <EditBranchDrawer
          visible={editBranchDrawerVisible}
          onClose={() => {
            setSelectedBranch(null);
            setEditBranchDrawerVisible(false);
          }}
          branch={selectedBranch}
        />
      )}
      {selectedBranch && (
        <DeleteBranchModal
          visible={deleteBranchModalVisible}
          onClose={() => {
            setSelectedBranch(null);
            setDeleteBranchModalVisible(false);
          }}
          branch={selectedBranch}
          club={clubId}
        />
      )}
    </Content>
  );
}
